import { useState } from 'react';

import { hasValue } from '@core/utils/equality';

import type { FilterByOpts, NamedFilterOpts } from '../../../utils/ApiClient';
import { MakeImmediateResult, type PossiblyAsyncResult } from '../../../utils/Query';
import type { TabLabel } from '../components/FilterTabs';

export type FilterTabLabelsOpts<TRow extends { id: string }, TAgg extends { id: string }> = {
  // When isActive, a tab provides `values` to be included in a table filter.
  // If `values` is `null`, the tab disables filtering when isActive (and thus
  // implicitly deactivates all tabs where `values` is not `null`).
  tabs: (aggregations: { [key: string]: TAgg }) => TabLabel<TRow>[];
  // The filter specification for which `tabs` will provide `values`.
  filterTemplate: Omit<NamedFilterOpts<TRow>, 'values'>;
  // Aggregated data, to be used for generating `tabs` when those are defined
  // via callback.
  aggregations?: PossiblyAsyncResult<{ [id: string]: TAgg }>;
  // Values of selected tabs that have been previously saved by the user,
  // and which should be restored when aggregations become available (to be
  // used when `tabs` is a callback).  If defined, this is used to override
  // the values of `isActive` in the initial tabs.  If undefined, the tabs'
  // `isActive` values are used instead.
  savedValues?: string[];
};

const activeTabValuesToFilters = <TRow extends { id: string }>(
  activeTabValues: (string | null)[],
  filterTemplate: Omit<FilterByOpts<TRow>, 'values'>,
): FilterByOpts<TRow>[] => {
  const values = activeTabValues.filter((v) => hasValue(v));
  return values.length > 0 ? [{ ...filterTemplate, values } as FilterByOpts<TRow>] : [];
};

/**
 * useFilterTabLabels is a hook that manages the TabLabel state for a DataTable
 * and its FilterTabs sub-component. It tracks the ac
 *
 * @returns A three-element array:
 *   - The currently active filters managed by this hook.
 *   - The current state of the tabs list managed by this hook.
 *   - A callback for toggling the state of a tab, which accepts a single tab
 *     and returns the resulting table filters managed by this hook for the
 *     next React rendering pass.
 */
export const useFilterTabLabels = <TRow extends { id: string }, TAgg extends { id: string }>(
  options?: FilterTabLabelsOpts<TRow, TAgg>,
): {
  filters: FilterByOpts<TRow>[];
  tabs: TabLabel<TRow>[];
  toggleTabActive: (tab: TabLabel<TRow>) => FilterByOpts<TRow>[];
  resetActiveTabs: () => FilterByOpts<TRow>[];
} => {
  const initialTabs = options?.tabs ?? ((): TabLabel<TRow>[] => []);
  const filterTemplate = options?.filterTemplate ?? null;
  const aggregations = options?.aggregations ?? MakeImmediateResult({});
  const savedValues = options?.savedValues;

  const rawTabs = aggregations.isSuccess ? initialTabs(aggregations.data) : [];
  const [activeTabValues, setActiveTabValues] = useState(
    rawTabs.filter((t) => t.isActive).map((t) => t.filterValue as string | null),
  );
  const [hasAppliedSavedValues, setHasAppliedSavedValues] = useState(false);

  const tabs = hasAppliedSavedValues
    ? rawTabs.map((t) => ({
        ...t,
        isActive: activeTabValues.includes(t.filterValue as string | null),
      }))
    : rawTabs;

  if (!hasValue(filterTemplate)) {
    return {
      filters: [],
      tabs: [],
      toggleTabActive: (): FilterByOpts<TRow>[] => [],
      resetActiveTabs: (): FilterByOpts<TRow>[] => [],
    };
  }

  const filters = activeTabValuesToFilters(activeTabValues, filterTemplate);

  const resetActiveTabs = (): FilterByOpts<TRow>[] => {
    const values = rawTabs
      .filter(({ isActive }) => isActive)
      .map(({ filterValue }) => filterValue as string | null);
    setActiveTabValues(values);
    return activeTabValuesToFilters(values, filterTemplate);
  };

  const toggleTabActive = (tab: TabLabel<TRow>): FilterByOpts<TRow>[] => {
    if (tabs === undefined) {
      // We should never get here... :upside_down_face:
      return [];
    }

    const tabToUpdate = tabs.find((t) => t.filterValue === tab.filterValue);
    if (!hasValue(tabToUpdate)) {
      return [];
    }

    // Update existing filters:
    // - If adding a new filter value, deactivate tabs without a filterValue.
    // - If activating a tab without a filterValue, deactivate all other tabs.
    // - If removing the last filter value, enable any tabs without a filterValue.
    // - If deactivating a tab without a filterValue, activate all tabs with a filterValue
    if (!hasValue(tab.filterValue) && !tab.isActive) {
      // Activating a no-filter tab: deactivate all other tabs.
      const newActiveTabValues = tabs
        .filter((t) => t === tabToUpdate)
        .map((t) => t.filterValue as null);
      setActiveTabValues(newActiveTabValues);
      return [];
    }
    if (!hasValue(tab.filterValue)) {
      // Deactivating a no-filter tab: activate all tabs with filter values.
      const newActiveTabValues = tabs
        .filter((t) => t.filterValue !== null)
        .map((t) => t.filterValue as string);
      setActiveTabValues(newActiveTabValues);
      return [{ ...filterTemplate, values: newActiveTabValues }];
    }
    if (!tab.isActive) {
      // Activating a tab with filter value: deactivate all no-filter tabs.
      const newActiveTabValues = tabs
        .filter((t) => t.filterValue !== null && (t.isActive || t === tabToUpdate))
        .map((t) => t.filterValue as string);
      setActiveTabValues(newActiveTabValues);
      return [{ ...filterTemplate, values: newActiveTabValues }];
    }
    // Deactivating a tab with filter value: If no other tabs with filter
    // values remain active, activate no-filter tabs.
    const isLastActiveTab =
      activeTabValues.length === 1 && activeTabValues[0] === tabToUpdate.filterValue;
    if (isLastActiveTab) {
      setActiveTabValues([null]);
      return [];
    }
    const newActiveTabValues = tabs
      .filter((t) => t !== tabToUpdate && t.isActive)
      .map((t) => t.filterValue as string | null);
    setActiveTabValues(newActiveTabValues);
    return isLastActiveTab ? [] : [{ ...filterTemplate, values: newActiveTabValues }];
  };

  // Handle initial state: Restore saved state, if any, otherwise configure the
  // default state instead of deferring to the user's changes in tableData
  // (because tableData won't have received user input yet, and won't otherwise
  // get initialized with the appropriate defaults).
  if (!hasAppliedSavedValues && aggregations.isSuccess) {
    setHasAppliedSavedValues(true);

    if (savedValues === undefined) {
      const defaultValues = tabs
        .filter(({ isActive }) => isActive)
        .map(({ filterValue }) => filterValue as string | null);
      setActiveTabValues(defaultValues);
      return { filters, tabs, toggleTabActive, resetActiveTabs };
    }

    const restoredTabs = tabs.map((tab) => ({
      ...tab,
      isActive: savedValues?.includes(tab.filterValue as string) ?? false,
    }));
    setActiveTabValues(savedValues);
    return { filters, tabs: restoredTabs, toggleTabActive, resetActiveTabs };
  }

  return { filters, tabs, toggleTabActive, resetActiveTabs };
};
